import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { einvoiceAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Eye, EyeSlash, ShieldCheck, Warning } from '@phosphor-icons/react';

export default function EInvoicingPage() {
  const [settings, setSettings] = useState(null);
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // gstin row being connected
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([einvoiceAPI.getSettings(), einvoiceAPI.listCredentials()]);
      setSettings(s.data);
      setCreds(c.data.credentials || []);
    } catch (e) { toast.error('Failed to load e-invoicing settings'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { await einvoiceAPI.updateSettings({
      enabled: next.enabled, generation_start_date: next.generation_start_date || '',
      override_eway_txn_type: next.override_eway_txn_type, edits_enabled: next.edits_enabled,
    }); } catch { toast.error('Failed to update'); load(); }
  };

  const openConnect = (row) => { setModal(row); setForm({ username: row.username || '', password: '' }); setShowPw(false); };

  const submitConnect = async () => {
    if (!form.username || !form.password) { toast.error('Enter username and password'); return; }
    setSaving(true);
    try {
      await einvoiceAPI.connect({ gstin: modal.gstin, username: form.username, password: form.password });
      toast.success(`IRP connected for ${modal.gstin}`);
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to connect'); }
    finally { setSaving(false); }
  };

  const disconnect = async (gstin) => {
    if (!window.confirm(`Disconnect IRP for ${gstin}?`)) return;
    try { await einvoiceAPI.disconnect(gstin); toast.success('Disconnected'); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading || !settings) return <div className="p-6"><div className="w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="einvoicing-page">
      <PageHeader title="e-Invoicing" subtitle="Connect Fintexa with the Government e-Invoice portal (IRP) to generate IRNs and signed QR codes" />
      <div className="page-content space-y-6 max-w-4xl">

        {settings.mocked && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="einvoice-mock-banner">
            <Warning size={18} className="mt-0.5 flex-shrink-0" />
            <span><strong>Sandbox / MOCK mode:</strong> The connection to the Government IRP is simulated. IRNs and QR codes are generated locally for testing. Provide your GSP API credentials to go live.</span>
          </div>
        )}

        {/* Master enable */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-[#0F4C81]" />
                <h3 className="font-semibold text-[#0F172A]">e-Invoicing</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${settings.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{settings.enabled ? 'ENABLED' : 'DISABLED'}</span>
              </div>
              <p className="text-sm text-[#64748B] mt-1">Turn on to push invoices to the IRP and generate IRNs.</p>
            </div>
            <Switch checked={settings.enabled} onCheckedChange={(v) => patchSettings({ enabled: v })} data-testid="einvoice-enable-toggle" />
          </div>
          {settings.enabled && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#E2E8F0] pt-4">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">e-Invoice Generation Start Date</label>
                <Input type="date" value={settings.generation_start_date || ''} onChange={(e) => patchSettings({ generation_start_date: e.target.value })} className="w-48" data-testid="einvoice-start-date" />
              </div>
              <div className="space-y-3">
                <label className="flex items-center justify-between text-sm">
                  <span className="text-[#0F172A]">Override E-Way Bill transaction type from IRP</span>
                  <Switch checked={settings.override_eway_txn_type} onCheckedChange={(v) => patchSettings({ override_eway_txn_type: v })} data-testid="einvoice-eway-toggle" />
                </label>
                <label className="flex items-center justify-between text-sm">
                  <span className="text-[#0F172A]">Allow edits to pushed transactions</span>
                  <Switch checked={settings.edits_enabled} onCheckedChange={(v) => patchSettings({ edits_enabled: v })} data-testid="einvoice-edits-toggle" />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Per-GSTIN IRP connections */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E2E8F0]">
            <h3 className="font-semibold text-[#0F172A]">Connect Fintexa with the IRP</h3>
            <p className="text-sm text-[#64748B]">Register Fintexa as your GSP on the IRP portal, then enter the API username/password generated there for each GSTIN.</p>
          </div>
          <table className="w-full text-sm" data-testid="einvoice-gstin-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] text-xs">
                <th className="text-left p-3">GSTIN</th>
                <th className="text-left p-3">State</th>
                <th className="text-left p-3">Username</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {creds.map((c) => (
                <tr key={c.gstin} className="border-t border-[#E2E8F0]" data-testid={`einvoice-gstin-${c.gstin}`}>
                  <td className="p-3 font-mono text-xs text-[#0F172A]">{c.gstin}</td>
                  <td className="p-3 text-[#64748B]">{c.state} ({c.state_code})</td>
                  <td className="p-3 text-[#64748B]">{c.username || '-'}</td>
                  <td className="p-3 text-center">
                    {c.status === 'connected' ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle size={14} weight="fill" /> Connected</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-500 text-xs"><XCircle size={14} /> Not Connected</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {c.status === 'connected' ? (
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openConnect(c)} data-testid={`reconfigure-${c.gstin}`}>Reconfigure</Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => disconnect(c.gstin)} data-testid={`disconnect-${c.gstin}`}>Disconnect</Button>
                      </div>
                    ) : (
                      <Button size="sm" className="h-7 text-xs bg-[#0F4C81] text-white" onClick={() => openConnect(c)} data-testid={`connect-${c.gstin}`}>Connect Now</Button>
                    )}
                  </td>
                </tr>
              ))}
              {creds.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">No GSTINs configured on your clinic accounts.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Credential modal */}
      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) setModal(null); }}>
        <DialogContent className="max-w-md" data-testid="irp-credential-modal">
          <DialogHeader><DialogTitle>Enter your IRP Credentials</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-[#64748B]">If you don't have GSP credentials, register Fintexa as a GSP on the IRP portal to generate a username & password for this GSTIN.</p>
            <div><label className="text-xs text-[#64748B] block mb-1">GSTIN</label><Input value={modal?.gstin || ''} disabled className="font-mono bg-gray-50" /></div>
            <div><label className="text-xs text-[#64748B] block mb-1">Username</label><Input value={form.username} onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))} data-testid="irp-username" /></div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Password</label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} data-testid="irp-password" />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPw(s => !s)}>{showPw ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={submitConnect} disabled={saving} className="bg-[#0F4C81] text-white" data-testid="irp-save-validate">{saving ? 'Validating...' : 'Save & Validate'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
